import uuidV4 from 'uuid/v4';

import appSettings from 'wanderu-config';
import {
    fetchAllTripsAction as fetchCachedJittedRoutedTrips,
} from 'wanderu-ducks-psearch';
import { i18nMoment } from 'wanderu-i18n';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';

import {
    actions as filterActions,
} from '../../appstate/filters';
import {
    actions as searchActions,
    selectors as searchSelectors,
} from '../../appstate/search';
import {
    TRIP_TTL_IN_MS,
} from '../../constants';
import {
    fetchCachedTripsAction,
} from '../fetchCachedTrips';
import {
    actions,
    namespace,
    reducer,
    selectors,
} from './base';
import {
    reset as resetCachedJittedRoutedTrips,
} from './reset';

const logger = Logger.getLogger('fetchAllTripsAction');

function fetch(opts) {
    return async (dispatch, getState) => {
        let cachedTripsPromise;
        let allTripsPromise;

        dispatch(actions.startWork(opts));

        /* batch id is used to tie together psearch calls made during an ssr cycle and psearch
         * calls made after the app boots up in the browser, for analytics purposes.  get rid
         * of it once we've booted the webapp and we're done with it, and use a new id for
         * future searches */
        let batchId = searchSelectors.selectServerBatchId(getState());

        if (!batchId) {
            /* search started in the browser; create a new batch id */
            batchId = uuidV4();
            SnowplowService.setBatchId(batchId);
            /* fetch cached trips to show something to the user more quickly */
            cachedTripsPromise = dispatch(fetchCachedTripsAction.fetch({ ...opts, batchId }));
        } else {
            SnowplowService.setBatchId(batchId);
            cachedTripsPromise = Promise.resolve();

            /* search started on the server; destroy the batch id */
            dispatch(searchActions.setServerBatchId(undefined));
        }

        let error;
        let meta = {
            expiresAt: i18nMoment().add(TRIP_TTL_IN_MS).valueOf(),
        };

        try {
            const psearchParams = {
                ...searchSelectors.selectPSearchParams(getState(), opts),
                batchId,
            };

            // eslint-disable-next-line no-async-promise-executor
            allTripsPromise = new Promise(async (resolve, reject) => {
                try {
                    await dispatch(fetchCachedJittedRoutedTrips.fetch(psearchParams));
                    dispatch(filterActions.updateFilterParams({
                        ...opts,
                        batchId,
                    }));
                    resolve();
                } catch (ex) {
                    reject(ex);
                }
            });

            await Promise.all([
                cachedTripsPromise,
                allTripsPromise,
            ]);
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.error('fetch', ex, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });

            meta = {
                expiresAt: undefined,
            };
            error = {
                code: ex.code,
                message: ex.message,
                name: ex.name,
            };
        } finally {
            dispatch(actions.endWork(opts, meta, error));
        }
    };
}

function reset(opts) {
    return (dispatch) => {
        dispatch(resetCachedJittedRoutedTrips(opts));
        dispatch(fetchCachedTripsAction.reset(opts));
        dispatch(actions.reset(opts));
    };
}

function expire(opts) {
    return actions.expire(opts);
}

const fetchAllTripsAction = {
    expire,
    fetch,
    reset,
    ...selectors,
};

export {
    fetchAllTripsAction,
};

export default {
    [namespace]: reducer,
};
